/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-apollo';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { useUnreadComments } from '@confluence/unread-comments';
import { useSessionData } from '@confluence/session-data';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { useEditorAnnotations } from '@confluence/inline-comments-hooks';
import { type GraphQLContentStatus } from '@confluence/comments-panel-queries';
import { fg } from '@confluence/feature-gating';

import { useCommentsData } from '../useCommentsData';
import { updateCommentsDataState } from '../helper/commentsDataHelper';

import type {
	useResolvedCommentsQueryResolvedCommentsQuery as useResolvedCommentsQueryResolvedCommentsQuery$data,
	useResolvedCommentsQueryResolvedCommentsQueryVariables as useResolvedCommentsQueryResolvedCommentsQuery$variables,
} from './__types__/useResolvedCommentsQueryResolvedCommentsQuery';

const useResolvedCommentsQueryResolvedCommentsQueryLegacy = gql`
	query useResolvedCommentsQueryResolvedCommentsQuery(
		$pageId: ID!
		$contentStatus: [GraphQLContentStatus]
	) {
		comments(
			pageId: $pageId
			contentStatus: $contentStatus
			depth: ROOT
			confluenceCommentFilter: { commentState: RESOLVED }
			singleThreaded: true
		) {
			nodes {
				id
				author {
					displayName
					profilePicture {
						path
					}
					permissionType
					... on KnownUser {
						accountId
					}
				}
				body(representation: ATLAS_DOC_FORMAT) {
					value
				}
				contentStatus
				createdAt {
					value
				}
				createdAtNonLocalized
				links {
					webui
					editui
				}
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineText
						inlineResolveProperties {
							isDangling
							resolvedTime
							resolvedFriendlyDate
							resolvedUser
							resolved
						}
					}
					... on FooterComment {
						commentResolveProperties {
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
				}
				parentId
				permissions {
					isEditable
					isRemovable
					isResolvable
				}
				version {
					by {
						displayName
					}
					when
					number
				}
				reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
					ari
					containerAri
					reactionsCount
					reactionsSummaryForEmoji {
						id
						emojiId
						count
						reacted
					}
				}
				replies {
					id
					author {
						displayName
						profilePicture {
							path
						}
						permissionType
						... on KnownUser {
							accountId
						}
					}
					body(representation: ATLAS_DOC_FORMAT) {
						value
					}
					contentStatus
					createdAt {
						value
					}
					createdAtNonLocalized
					links {
						webui
						editui
					}
					location {
						type
						... on InlineComment {
							inlineMarkerRef
							inlineText
							inlineResolveProperties {
								isDangling
								resolvedTime
								resolvedFriendlyDate
								resolvedUser
								resolved
							}
						}
						... on FooterComment {
							commentResolveProperties {
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
					}
					parentId
					permissions {
						isEditable
						isRemovable
						isResolvable
					}
					version {
						by {
							displayName
						}
						when
						number
					}
					reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
						ari
						containerAri
						reactionsCount
						reactionsSummaryForEmoji {
							id
							emojiId
							count
							reacted
						}
					}
				}
			}
		}
	}
`;

const useResolvedCommentsQueryResolvedCommentsQueryUpdated = gql`
	query useResolvedCommentsQueryConfluenceResolvedCommentsQuery(
		$pageId: ID!
		$contentStatus: [GraphQLContentStatus]
	) {
		comments(
			pageId: $pageId
			contentStatus: $contentStatus
			depth: ROOT
			confluenceCommentFilter: { commentState: RESOLVED }
			singleThreaded: true
		) {
			nodes {
				id
				author {
					displayName
					profilePicture {
						path
					}
					permissionType
					... on KnownUser {
						accountId
					}
					... on User {
						accountId
					}
				}
				body(representation: ATLAS_DOC_FORMAT) {
					value
				}
				contentStatus
				createdAt {
					value
				}
				createdAtNonLocalized
				links {
					webui
					editui
				}
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineText
						inlineResolveProperties {
							isDangling
							resolvedTime
							resolvedFriendlyDate
							resolvedUser
							resolved
						}
					}
					... on FooterComment {
						commentResolveProperties {
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
				}
				parentId
				permissions {
					isEditable
					isRemovable
					isResolvable
				}
				version {
					by {
						displayName
					}
					when
					number
				}
				reactionsSummary: confluenceReactionsSummary(childType: COMMENT) {
					reactionsSummary {
						ari
						containerAri
						reactionsCount
						reactionsSummaryForEmoji {
							id
							emojiId
							count
							reacted
						}
					}
				}
				replies {
					id
					author {
						displayName
						profilePicture {
							path
						}
						permissionType
						... on KnownUser {
							accountId
						}
						... on User {
							accountId
						}
					}
					body(representation: ATLAS_DOC_FORMAT) {
						value
					}
					contentStatus
					createdAt {
						value
					}
					createdAtNonLocalized
					links {
						webui
						editui
					}
					location {
						type
						... on InlineComment {
							inlineMarkerRef
							inlineText
							inlineResolveProperties {
								isDangling
								resolvedTime
								resolvedFriendlyDate
								resolvedUser
								resolved
							}
						}
						... on FooterComment {
							commentResolveProperties {
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
					}
					parentId
					permissions {
						isEditable
						isRemovable
						isResolvable
					}
					version {
						by {
							displayName
						}
						when
						number
					}
					reactionsSummary: confluenceReactionsSummary(childType: COMMENT) {
						reactionsSummary {
							ari
							containerAri
							reactionsCount
							reactionsSummaryForEmoji {
								id
								emojiId
								count
								reacted
							}
						}
					}
				}
			}
		}
	}
`;

export const formatResolvedCommentsDataResponse = (data: any) => {
	if (!fg('use_confluence_reactions_summary_endpoint') || !data) {
		return data;
	}

	const transformedNodes: any[] = data.comments.nodes.map((node: any) => {
		const transformedReplies: any[] = node.replies.map((reply: any) => ({
			...reply,
			reactionsSummary: reply.reactionsSummary.reactionsSummary?.[0],
		}));
		return {
			...node,
			reactionsSummary: node.reactionsSummary.reactionsSummary?.[0],
			replies: transformedReplies,
		};
	});
	return {
		comments: {
			nodes: transformedNodes,
		},
	};
};

export const useResolvedCommentsQuery = ({
	pageId,
	skip = false,
}: {
	pageId: string;
	skip?: boolean;
}) => {
	// The query loading was not sticking around long enough or triggering at the right times
	// so I created a loading state that will set after the query is successful/fails
	const [loading, setLoading] = useState(true);
	const [{ commentsDataMap }, { addNewCommentThreads, setCommentsDataMap }] = useCommentsData();
	const [{ readCommentsListState }] = useUnreadComments();
	const [{ annotations }] = useEditorAnnotations();

	const pageMode = useGetPageMode();
	const { userId: currentUserId } = useSessionData();

	const annotationsInEditorDoc = useMemo(() => {
		return new Set(annotations);
	}, [annotations]);

	// Determine if the query should be skipped
	const skipQuery = !pageId || skip;

	const hasAlreadyFetchedInitialData =
		Object.keys(commentsDataMap.inline).length > 0 ||
		Object.keys(commentsDataMap.general).length > 0;

	const useResolvedCommentsQueryResolvedCommentsQuery = fg(
		'use_confluence_reactions_summary_endpoint',
	)
		? useResolvedCommentsQueryResolvedCommentsQueryUpdated
		: useResolvedCommentsQueryResolvedCommentsQueryLegacy;

	// Execute the query
	const {
		data,
		loading: queryLoading,
		error,
		refetch,
	} = useQuery<
		useResolvedCommentsQueryResolvedCommentsQuery$data,
		useResolvedCommentsQueryResolvedCommentsQuery$variables
	>(useResolvedCommentsQueryResolvedCommentsQuery, {
		variables: {
			pageId,
			contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
		},
		// Skip is not respected after the initial call: https://github.com/apollographql/apollo-client/issues/6190#issuecomment-649933628
		fetchPolicy: skipQuery ? 'cache-only' : 'cache-and-network', // We want to utilize the cache as much as possible, but keep the list up to date if it changes
		errorPolicy: fg('confluence_frontend_handle_reactions_errors') ? 'all' : 'none',
		skip: skipQuery,
		onCompleted: (result) => {
			updateCommentsDataState({
				data: formatResolvedCommentsDataResponse(result),
				readCommentsListState,
				updateData: hasAlreadyFetchedInitialData ? addNewCommentThreads : setCommentsDataMap,
				annotationsInEditorDoc,
				pageMode,
				currentUserId: currentUserId ?? '',
				isOpen: false,
			});
			setLoading(false);
		},
		onError: fg('confluence_frontend_handle_reactions_errors')
			? undefined
			: (err) => {
					// If the error is related to reactionsSummary returning null, just mark it handled
					// or if the error is related to cc-pages not being reachable, just mark it handled
					if (err.message.search(/reactions/i) !== -1 || err.message.search(/cc-pages/i) !== -1) {
						markErrorAsHandled(err);
					}
					setLoading(false);
				},
	});
	useEffect(() => {
		if (error && fg('confluence_frontend_handle_reactions_errors')) {
			// If the error is related to reactionsSummary returning null, just mark it handled
			// or if the error is related to cc-pages not being reachable, just mark it handled
			if (error.message.search(/reactions/i) !== -1 || error.message.search(/cc-pages/i) !== -1) {
				markErrorAsHandled(error);
			}
			setLoading(false);
		}
	}, [error]);
	useEffect(() => {
		if (queryLoading) {
			setLoading(true);
		}
	}, [queryLoading]);

	return {
		loading: !skipQuery ? loading : false, // If we skip the query make sure loading is false
		data: formatResolvedCommentsDataResponse(data),
		error,
		refetch,
	};
};
